import { Capabilities, type ScheduledApp } from '@crossnokaye/typescript-sdk/models/components';

import type {
  CompressorEnergyAIRefs,
  CompressorScheduleForecast,
  CompressorScheduleStep,
  DisplayRef,
} from '@repo/sdk/models/components';

import { ScheduledAppKind } from '@/constants/scheduled-apps';

import type {
  CurrentEnergyAISegment,
  EnergyAICompressorState,
  EnergyAIRampDirection,
  EnergyAIStageRow,
  EnergyAIValueReader,
} from './compressor-energy-ai.types';
import { CompressorSequencerStage } from '../helpers';

interface GetEnergyAIStageRowsProps {
  compressorOrder: string[];
  energyAICompressors: CompressorEnergyAIRefs[] | undefined;
  forecast: CompressorScheduleForecast | undefined;
  now: number;
  readValue: EnergyAIValueReader;
}

const TRANSITION_RAMPING_UP = 'ramping_up';
const TRANSITION_RAMPING_DOWN = 'ramping_down';

const readNumber = (readValue: EnergyAIValueReader, displayRef: DisplayRef | undefined) => {
  const value = readValue(displayRef);
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
};

export const getEnergyAIStateFromStageValue = (
  stageValue: number | undefined
): EnergyAICompressorState => {
  switch (stageValue) {
    case CompressorSequencerStage.UNAVAILABLE:
      return 'unavailable';
    case CompressorSequencerStage.OFF:
      return 'off';
    case CompressorSequencerStage.ON:
      return 'on';
    case CompressorSequencerStage.TRIM:
      return 'trim';
    default:
      return 'unknown';
  }
};

export const getEnergyAIRampDirection = (
  segment: CurrentEnergyAISegment | undefined
): EnergyAIRampDirection => {
  if (segment?.transition === TRANSITION_RAMPING_UP) {
    return 'up';
  }
  if (segment?.transition === TRANSITION_RAMPING_DOWN) {
    return 'down';
  }
  return 'none';
};

const clampProgress = (value: number) => Math.min(1, Math.max(0, value));

export const computeEnergyAIRampProgress = ({
  direction,
  rampDownTimeSecs,
  rampDownTimerSecs,
  rampUpTimeSecs,
  runTimeSecs,
  state,
}: {
  direction: EnergyAIRampDirection;
  rampDownTimeSecs: number | undefined;
  rampDownTimerSecs: number | undefined;
  rampUpTimeSecs: number | undefined;
  runTimeSecs: number | undefined;
  state: EnergyAICompressorState;
}): number | undefined => {
  if (direction === 'up') {
    if (runTimeSecs === undefined || !rampUpTimeSecs) return undefined;
    return clampProgress(runTimeSecs / rampUpTimeSecs);
  }
  if (direction === 'down') {
    if (rampDownTimerSecs === undefined || !rampDownTimeSecs) return undefined;
    return clampProgress(1 - rampDownTimerSecs / rampDownTimeSecs);
  }
  if (state === 'off' || state === 'unavailable') {
    return 0;
  }
  if (state === 'on' || state === 'trim') {
    return 1;
  }
  return undefined;
};

const sortForecastSteps = (steps: CompressorScheduleStep[]): CompressorScheduleStep[] => {
  return [...steps].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
};

export const getCurrentEnergyAISegment = (
  sortedSteps: CompressorScheduleStep[],
  compressors: string[],
  compressorName: string,
  now: number
): CurrentEnergyAISegment | undefined => {
  if (sortedSteps.length === 0) {
    return undefined;
  }

  const compressorIndex = compressors.indexOf(compressorName);
  if (compressorIndex < 0) {
    return undefined;
  }

  // Defaults to the first step, which is also the answer when `now` precedes the
  // forecast window: no step matches below and the initial values stand.
  let currentStep = sortedSteps[0];
  let nextStep = sortedSteps[1];

  for (let i = 0; i < sortedSteps.length; i++) {
    const step = sortedSteps[i];
    const stepTime = step.timestamp.getTime();
    const next = sortedSteps[i + 1];
    const nextTime = next?.timestamp.getTime() ?? Number.POSITIVE_INFINITY;

    if (now >= stepTime && now < nextTime) {
      currentStep = step;
      nextStep = next;
      break;
    }
  }

  const state = currentStep.states[compressorIndex];
  if (!state) {
    return undefined;
  }

  return {
    compressorName,
    endTime: nextStep?.timestamp.getTime(),
    stageValue: state.value,
    startTime: currentStep.timestamp.getTime(),
    transition: state.transition,
  };
};

/**
 * Joins a sequencer table row to its compressor's live refs.
 *
 * This is the one join in this feature that crosses data sources: the row name
 * comes from the sequence table (a control-system setting) while both candidate
 * keys come from the facility's enhanced-device list. Nothing guarantees they
 * agree, and `SequenceItem` carries no id, so a name is the only key either side
 * exposes - a sounder join would need an identifier the payload does not have.
 *
 * The comparison is deliberately strict. Trimming or case-folding would not fix
 * genuinely different names; it would only shrink the set of mismatches while
 * making the survivors harder to reason about. A miss is silent by design (the
 * stage fill comes from the forecast and still renders, only the ramp slider is
 * suppressed), so `useCompressorEnergyAIStageRows` reports the total-miss case
 * where nothing at all resolves.
 */
const findEnergyAIRefs = (
  energyAICompressors: CompressorEnergyAIRefs[] | undefined,
  compressorName: string
) => {
  return energyAICompressors?.find((refs) => {
    return refs.compressorName === compressorName || refs.deviceAlias === compressorName;
  });
};

const getTargetLabel = (direction: EnergyAIRampDirection) => {
  if (direction === 'up') {
    return 'toward on';
  }
  if (direction === 'down') {
    return 'toward off';
  }
  return undefined;
};

export const getEnergyAIStageRows = ({
  compressorOrder,
  energyAICompressors,
  forecast,
  now,
  readValue,
}: GetEnergyAIStageRowsProps): EnergyAIStageRow[] => {
  const sortedSteps = forecast?.steps.length ? sortForecastSteps(forecast.steps) : [];
  const compressors = forecast?.compressors ?? [];

  return compressorOrder.map((compressorName) => {
    const segment = getCurrentEnergyAISegment(sortedSteps, compressors, compressorName, now);
    const refs = findEnergyAIRefs(energyAICompressors, compressorName);
    const state = getEnergyAIStateFromStageValue(segment?.stageValue);
    const direction = getEnergyAIRampDirection(segment);
    const progress = computeEnergyAIRampProgress({
      direction,
      rampDownTimeSecs: readNumber(readValue, refs?.rampDownTimeSecs),
      rampDownTimerSecs: readNumber(readValue, refs?.rampDownTimerSecs),
      rampUpTimeSecs: readNumber(readValue, refs?.rampUpTimeSecs),
      runTimeSecs: readNumber(readValue, refs?.runTimeSecs),
      state,
    });

    return {
      compressorName,
      direction: progress === undefined ? 'none' : direction,
      progress,
      refs,
      segment,
      state,
      targetLabel: getTargetLabel(progress === undefined ? 'none' : direction),
    };
  });
};

/**
 * True when the BFF supplied compressor refs and not one sequencer row matched
 * any of them - the unambiguous shape of a broken name join.
 *
 * A partial miss is not reported: a compressor can legitimately be absent from
 * the Energy AI list while its neighbours resolve, and only a total miss
 * distinguishes "these names do not line up" from "this one compressor is not
 * controlled".
 */
export const hasUnmatchedEnergyAIRefs = ({
  energyAICompressors,
  rows,
}: {
  energyAICompressors: CompressorEnergyAIRefs[] | undefined;
  rows: EnergyAIStageRow[];
}): boolean => {
  if (!energyAICompressors?.length || rows.length === 0) {
    return false;
  }

  return rows.every((row) => row.refs === undefined);
};

/**
 * The compressor sequencer Energy AI column is for facilities that have the
 * mpc_compressor_sequencing app turned on with scheduling enabled. Matches
 * GetActiveEaiApps: a missing status is excluded, and Pending, Running, and
 * Error all count as enabled.
 */
export const isCompressorSequencingEnergyAIEnabled = (
  apps: ScheduledApp[] | undefined
): boolean => {
  return (
    apps?.some((app) => {
      const statusType = app.status?.status.type;
      return (
        app.package.kind === ScheduledAppKind.MpcCompressorSequencing &&
        statusType !== undefined &&
        statusType !== 'Disabled' &&
        app.capabilityStatuses?.[Capabilities.Scheduling] === true
      );
    }) ?? false
  );
};

export const formatEnergyAIRampAriaLabel = (row: EnergyAIStageRow): string => {
  const stateLabel = row.state === 'unknown' ? 'unknown state' : row.state;
  const targetLabel = row.targetLabel ? ` ${row.targetLabel}` : '';
  // Only a ramping compressor has a meaningful completion percentage. A steady
  // On/Trim row carries progress 1, which would otherwise announce as "100 percent
  // complete" and read as a transition that just finished.
  const progressText =
    row.direction === 'none' || row.progress === undefined
      ? ''
      : `, ${Math.round(row.progress * 100)} percent complete`;

  return `${row.compressorName}: Energy AI ${stateLabel}${targetLabel}${progressText}`;
};
